<script setup>
import { computed, defineEmits, defineProps, ref } from "vue";
import UtilDateTime from "@/utils/UtilDateTime";

const props = defineProps({
  carregando: {
    type: Boolean,
    default: false
  },
  orcamentos: {
    type: Array,
    default: () => []
  },
  clientes: {
    type: Array,
    default: () => []
  },
  usuarioNome: {
    type: String,
    default: ""
  },
  podeCriar: {
    type: Boolean,
    default: true
  },
  podeEditar: {
    type: Boolean,
    default: true
  },
  podeDeletar: {
    type: Boolean,
    default: true
  }
});

const emits = defineEmits([
  "novo",
  "visualizar",
  "editar",
  "recarregar"
]);

const filtroCodigo = ref(null);
const filtroCliente = ref(null);
const filtroStatus = ref(null);

const normalizarTexto = (valor = "") => String(valor || "").trim().toUpperCase();

const obterIdClienteItem = (item = {}) => {
  if (typeof item?.cliente === "object") return item?.cliente?.id || item?.cliente?._id || null;
  return item?.cliente || item?.clienteID || item?.customerId || null;
};

const resolverClienteCadastro = (item = {}) => {
  const clientes = Array.isArray(props.clientes) ? props.clientes : [];
  const clienteId = obterIdClienteItem(item);

  if (clienteId) {
    const porId = clientes.find((cliente) => {
      const id = cliente?.id || cliente?._id;
      return String(id || "") === String(clienteId || "");
    });
    if (porId) return porId;
  }

  const nomeItem = normalizarTexto(
    item?.clienteNome || item?.customerName || item?.cliente?.nomeFantasia || item?.cliente?.razaoSocial
  );

  if (nomeItem) {
    const porNome = clientes.find((cliente) => {
      const nomeCliente = normalizarTexto(cliente?.nomeFantasia || cliente?.razaoSocial || cliente?.nome);
      return nomeCliente === nomeItem;
    });
    if (porNome) return porNome;
  }

  return null;
};

const obterCodigoCliente = (item = {}) => {
  const clienteCadastro = resolverClienteCadastro(item);
  return (
    item?.clienteCodigo ||
    item?.customerCode ||
    item?.cliente?.codigo ||
    item?.cliente?.code ||
    clienteCadastro?.codigo ||
    clienteCadastro?.code ||
    ""
  );
};

const obterNomeCliente = (item = {}) => {
  const clienteCadastro = resolverClienteCadastro(item);
  return (
    item?.clienteNome ||
    item?.customerName ||
    item?.cliente?.nomeFantasia ||
    item?.cliente?.razaoSocial ||
    clienteCadastro?.nomeFantasia ||
    clienteCadastro?.razaoSocial ||
    clienteCadastro?.nome ||
    ""
  );
};

const chaveCliente = (item = {}) => {
  const codigo = normalizarTexto(obterCodigoCliente(item));
  const nome = normalizarTexto(obterNomeCliente(item));
  return `${codigo}|${nome}`;
};


// Exibe apenas o código no filtro de Nº
const itemTextoCodigo = (item = {}) => {
  return item?.code || item?.numeroProposta || item?.numeroSolicitacao || "-";
};

const chaveCodigo = (item = {}) =>
  normalizarTexto(item?.code || item?.numeroProposta || item?.numeroSolicitacao || "");

const itemTextoCliente = (item = {}) => {
  const codigoCliente = obterCodigoCliente(item);
  const nomeCliente = obterNomeCliente(item);
  if (codigoCliente && nomeCliente) return `${codigoCliente} - ${nomeCliente}`;
  return nomeCliente || codigoCliente || "-";
};

const statusOpcoes = computed(() => {
  const mapa = new Map();
  (props.orcamentos || []).forEach((item) => {
    const descricao = String(item?.statusDescription || item?.status || "").trim();
    if (!descricao) return;
    const chave = descricao.toUpperCase();
    if (!mapa.has(chave)) {
      mapa.set(chave, { id: item?.status || descricao, description: descricao });
    }
  });
  return Array.from(mapa.values()).sort((a, b) =>
    String(a?.description || "").localeCompare(String(b?.description || ""))
  );
});

const orcamentosFiltrados = computed(() => {
  return (props.orcamentos || []).filter((item) => {
    const validoCodigo = !filtroCodigo.value || chaveCodigo(item) === chaveCodigo(filtroCodigo.value);
    const validoCliente = !filtroCliente.value || chaveCliente(item) === chaveCliente(filtroCliente.value);
    const statusItem = String(item?.statusDescription || item?.status || "").trim().toUpperCase();
    const statusFiltro = String(filtroStatus.value?.description || filtroStatus.value?.status || "").trim().toUpperCase();
    const validoStatus = !filtroStatus.value || (statusItem && statusItem === statusFiltro);
    return validoCodigo && validoCliente && validoStatus;
  });
});

const headers = [
  { text: "", value: "controles", sortable: false, width: "160px" },
  { text: "Numero Proposta", value: "numeroProposta" },
  { text: "Revisão", value: "revisao" },
  { text: "Sigla", value: "sigla" },
  { text: "Cliente", value: "clienteNome" },
  { text: "Referencia do Cliente", value: "referenciaCliente" },
  { text: "Contato Comercial", value: "contatoNome" },
  { text: "Contato Técnico", value: "contatoTecnicoNome" },
  { text: "Data Geração", value: "dateGenerate" },
  { text: "Valor de Venda s/IPI c/ICMS", value: "precoVenda_sIPI_cICMS" },
  { text: "Descrição do Status", value: "statusDescription" }
];

const formatarData = (data) => {
  if (!data) return "";
  return UtilDateTime.toFormatBrazil({ dateTime: data }) || data;
};

const formatarMoeda = (valor) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(valor || 0));
};

const corStatus = (statusDescricao) => {
  const texto = String(statusDescricao || "").toUpperCase();
  if (texto.includes("APROV")) return "success";
  if (texto.includes("PERD") || texto.includes("RECUS")) return "error";
  if (texto.includes("ENVIAD") || texto.includes("EXECU")) return "info";
  if (texto.includes("CANCEL")) return "grey";
  return "warning";
};

const textoStatus = (item = {}) =>
  String(item?.statusDescription || item?.status || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const statusCancelado = (item = {}) => textoStatus(item).includes("CANCEL");

const statusPedidoColocado = (item = {}) => textoStatus(item).includes("PEDIDO COLOCADO");

const bloqueiaAcoes = (item = {}) => statusCancelado(item) || statusPedidoColocado(item);
</script>

<template>
  <v-card class="c-card">
    <v-card-title class="c-card__header">
      <div class="c-header__buscas">
        <v-autocomplete
          class="c-busca__input"
          label="N°"
          :items="props.orcamentos"
          :item-text="itemTextoCodigo"
          return-object
          clearable
          outlined
          hide-details
          v-model="filtroCodigo"
        />

        <v-autocomplete
          class="c-busca__input"
          label="Cliente"
          :items="props.orcamentos"
          :item-text="itemTextoCliente"
          return-object
          clearable
          outlined
          hide-details
          v-model="filtroCliente"
        />

        <v-autocomplete
          class="c-busca__input"
          label="Status"
          :items="statusOpcoes"
          item-text="description"
          return-object
          clearable
          outlined
          hide-details
          v-model="filtroStatus"
        />
      </div>

      <v-chip class="c-header__usuario" label>
        <v-icon left>mdi-account-circle-outline</v-icon>
        Usuario: {{ props.usuarioNome || "-" }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <div class="c-acoes mb-3">
        <v-btn
          v-if="props.podeCriar"
          color="primary"
          dark
          :loading="props.carregando"
          @click="emits('novo')"
        >
          <v-icon left>mdi-plus</v-icon>
          Nova Solicitação
        </v-btn>
        <v-btn outlined color="primary" :loading="props.carregando" @click="emits('recarregar')">
          <v-icon left>mdi-refresh</v-icon>
          Recarregar
        </v-btn>
      </div>

      <div class="c-grid-wrap">
        <v-data-table
          :headers="headers"
          :items="orcamentosFiltrados"
          :loading="props.carregando"
          item-key="_listKey"
          :footer-props="{ 'items-per-page-text': 'Linhas por página:' }"
          page-text="{0}-{1} de {2}"
          mobile-breakpoint="0"
          class="c-grid elevation-1"
        >
        <!-- Coluna de Controles (botões inline) -->
        <template v-slot:item.controles="{ item }">
          <div class="c-controles">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1"
                  fab
                  dark
                  small
                  color="#32434D"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="emits('visualizar', item)"
                >
                  <v-icon dark>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Visualizar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="!bloqueiaAcoes(item)"
                  class="mx-1"
                  fab
                  dark
                  small
                  color="#32434D"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="emits('editar', item)"
                >
                  <v-icon dark>mdi-file-edit-outline</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Sigla -->
        <template v-slot:item.sigla="{ item }">
          <div class="c-sigla">
            {{
              (
                (item.tipoDeEquipamentoSigla || item._mirror_tipoDeEquipamento?.sigla || item._mirror_tipoDeEquipamento?.codigo || item.sigla) +
                (item.tipoDeEquipamentoDescricao && item.tipoDeEquipamentoDescricao !== item.tipoDeEquipamentoSigla ? ' - ' + item.tipoDeEquipamentoDescricao : '')
              ) || '-'
            }}
          </div>
        </template>

        <!-- Numero Proposta -->
        <template v-slot:item.numeroProposta="{ item }">
          {{ item.numeroProposta || item.numeroSolicitacao || item.code || "-" }}
        </template>

        <!-- Referencia do Cliente -->
        <template v-slot:item.referenciaCliente="{ item }">
          {{ item.referenciaCliente || item.cliente_referencia || "-" }}
        </template>

        <!-- Contato Comercial -->
        <template v-slot:item.contatoNome="{ item }">
          {{ item.contatoNome || item.contatoComercialNome || "-" }}
        </template>

        <!-- Contato Técnico -->
        <template v-slot:item.contatoTecnicoNome="{ item }">
          {{ item.contatoTecnicoNome || "-" }}
        </template>

        <!-- Data Geração -->
        <template v-slot:item.dateGenerate="{ item }">
          {{ formatarData(item.dateGenerate) }}
        </template>

        <!-- Valor -->
        <template v-slot:item.precoVenda_sIPI_cICMS="{ item }">
          {{ formatarMoeda(item.precoVenda_sIPI_cICMS || item.valorEstimado) }}
        </template>

        <!-- Status -->
        <template v-slot:item.statusDescription="{ item }">
          <v-chip small dark :color="corStatus(item.statusDescription || item.status)">
            {{ item.statusDescription || item.status || '-' }}
          </v-chip>
        </template>
        </v-data-table>

      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.c-card {
  min-width: 0;
}

.c-card__header {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
  min-width: 0;
}

.c-header__buscas {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  min-width: 0;
}

@media (max-width: 1200px) {
  .c-header__buscas {
    grid-template-columns: 1fr;
  }
}

.c-header__usuario {
  min-height: 48px;
}

.c-acoes {
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
}

.c-grid-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

/* Header igual ao Job: fundo azul escuro #230bc1, texto branco */
.c-grid :deep(thead tr th) {
  background-color: #230bc1 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  white-space: nowrap;
}

/* Ícone de ordenação branco */
.c-grid :deep(thead tr th .v-icon) {
  color: #ffffff !important;
}

/* Overflow horizontal */
.c-grid :deep(.v-data-table__wrapper) {
  overflow-x: auto;
  min-width: 100%;
}

.c-grid :deep(table) {
  width: max-content;
  min-width: 1700px;
  max-width: none;
  table-layout: auto;
}

.c-grid :deep(td),
.c-grid :deep(th) {
  white-space: nowrap;
}

.c-grid :deep(th:first-child),
.c-grid :deep(td:first-child) {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
}

.c-grid :deep(thead tr th:first-child) {
  background-color: #230bc1 !important;
  z-index: 3;
}

.c-grid :deep(td:not(:first-child)) {
  white-space: normal;
  word-break: break-word;
}

/* Botões inline no estilo Job */
.c-controles {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 0;
  flex-wrap: nowrap;
  min-width: 140px;
}

.c-sigla {
  min-width: 140px;
  white-space: normal;
  line-height: 1.25;
}

@media (max-width: 1200px) {
  .c-card__header {
    grid-template-columns: 1fr;
  }

  .c-header__usuario {
    justify-self: start;
  }
}

@media (max-width: 960px) {
  .c-header__buscas {
    grid-template-columns: 1fr;
  }
}
</style>
